import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, MessageCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f38464ee-0c0b-455c-af8c-d4927305d805/artifacts/0w6s5t9g_Quran%20online.png";
const INSTAGRAM_URL = "https://www.instagram.com/quranonline_sverige";

const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer className="footer-bg text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Quran Online" className="h-12 w-auto" />
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gold transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="footer-instagram-icon">
                <svg viewBox="0 0 24 24" width="16" height="16" className="fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://wa.me/46733874447" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gold transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="footer-whatsapp-icon">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gold text-sm font-semibold uppercase tracking-wider mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('nav.programs')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/quran" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('nav.quran')}
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {t('nav.book')}
                </Link>
              </li>
              <li>
                <Link to="/tajweed-bok" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Tajweed Guide
                </Link>
              </li>
              <li>
                <Link to="/noorania-bok" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Quran Online Method
                </Link>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-gold text-sm font-semibold uppercase tracking-wider mb-6">
              {t('nav.programs')}
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Quran Online Method
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Tajweed
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Hifdh
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors text-sm">
                  {language === 'sv' ? 'Privatlektioner (1-on-1)' : 'Private Lessons (1-on-1)'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gold text-sm font-semibold uppercase tracking-wider mb-6">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail size={16} className="text-gold" />
                <a href="mailto:info@quranonline.se" className="hover:text-white transition-colors">
                  info@quranonline.se
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail size={16} className="text-gold" />
                <a href="mailto:trial@quranonline.se" className="hover:text-white transition-colors">
                  trial@quranonline.se
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={16} className="text-gold" />
                <a href="tel:+46733874447" className="hover:text-white transition-colors">
                  +46 73 387 44 47
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <svg viewBox="0 0 24 24" width="16" height="16" className="text-gold fill-current flex-shrink-0"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="instagram-link">
                  @quranonline_sverige
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} Quran Online. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-4">
            <Link to="/policy" className="text-gray-600 text-sm hover:text-gray-400 transition-colors" data-testid="policy-link">
              {language === 'sv' ? 'Lektionspolicy' : 'Lesson Policy'}
            </Link>
            <Link to="/privacy" className="text-gray-600 text-sm hover:text-gray-400 transition-colors" data-testid="privacy-link">
              {language === 'sv' ? 'Integritetspolicy' : 'Privacy Policy'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
